<template>
  <v-card class="printers-card" elevation="4">
    <v-card-title>
      <h2 class="section-title">Drucker Status</h2>
    </v-card-title>
    <v-card-text>
      <v-row>
        <v-col 
          v-for="printer in printers" 
          :key="printer.name"
          cols="12" 
          sm="6" 
          md="4"
        >
          <v-card
            :color="getPrinterCardColor(printer)"
            dark
            class="printer-item"
            elevation="2"
          >
            <v-card-text class="pa-3">
              <div class="d-flex align-center justify-space-between mb-2">
                <v-icon left>mdi-printer</v-icon>
                <v-chip
                  :color="getPrinterStatusColor(printer)"
                  small
                  class="status-chip"
                >
                  {{ getPrinterStatusText(printer) }}
                </v-chip>
              </div>
              <div class="printer-name font-weight-bold">{{ printer.name }}</div>
              <v-progress-linear
                v-if="printer.progress !== undefined"
                :value="printer.progress"
                :color="getProgressColor(printer.progress)"
                class="mt-2 mb-1"
                height="6"
                rounded
              ></v-progress-linear>
              <div class="printer-stats mt-2">
                <div class="d-flex justify-space-between caption">
                  <span>Jobs: {{ printer.jobs || 0 }}</span>
                  <span>Queue: {{ printer.queue || 0 }}</span>
                </div>
                <div v-if="printer.lastPrint" class="caption grey--text mt-1">
                  Letzter Druck: {{ formatTime(printer.lastPrint) }}
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      
  
    </v-card-text>
  </v-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';

export default {
  name: 'PrintersCard',
  
  components: {
    apexchart: VueApexCharts,
  },
  
  props: {
    printers: {
      type: Array,
      default: () => []
    },
    printHistory: {
      type: Array,
      default: () => []
    }
  },
  
  data() {
    return {
      printChartOptions: {
        chart: {
          type: 'line',
          background: 'transparent',
          toolbar: { show: false }
        },
        theme: {
          mode: 'dark'
        },
        stroke: {
          curve: 'smooth',
          width: 2
        },
        xaxis: {
          type: 'datetime',
          labels: {
            style: { colors: '#fff' },
            datetimeFormatter: {
              hour: 'HH:mm'
            }
          }
        },
        yaxis: {
          title: {
            text: 'Drucke/Stunde',
            style: { color: '#fff' }
          },
          labels: {
            style: { colors: '#fff' }
          }
        },
        colors: ['#00E396', '#FEB019'],
        legend: {
          position: 'top',
          labels: { colors: '#fff' }
        },
        grid: {
          borderColor: 'rgba(255, 255, 255, 0.1)'
        },
        tooltip: {
          theme: 'dark'
        }
      },
      printChartSeries: [],
      
      jobChartOptions: {
        chart: {
          type: 'bar',
          background: 'transparent',
          toolbar: { show: false },
          stacked: true
        },
        theme: {
          mode: 'dark'
        },
        xaxis: {
          categories: [],
          labels: {
            style: { colors: '#fff' }
          }
        },
        yaxis: {
          title: {
            text: 'Jobs',
            style: { color: '#fff' }
          },
          labels: {
            style: { colors: '#fff' }
          }
        },
        colors: ['#00E396', '#FEB019', '#FF4560'],
        legend: {
          position: 'top',
          labels: { colors: '#fff' }
        },
        dataLabels: {
          enabled: false
        },
        plotOptions: {
          bar: {
            borderRadius: 4
          }
        },
        grid: {
          borderColor: 'rgba(255, 255, 255, 0.1)'
        }
      },
      jobChartSeries: [{
        name: 'Erfolgreich',
        data: []
      }, {
        name: 'In Warteschlange',
        data: []
      }, {
        name: 'Fehler',
        data: []
      }]
    };
  },
  
  watch: {
    printers: {
      immediate: true,
      deep: true,
      handler() {
        this.updateCharts();
      }
    },
    printHistory: {
      immediate: true,
      deep: true,
      handler() {
        this.updatePrintHistoryChart();
      }
    }
  },
  
  methods: {
    getPrinterCardColor(printer) {
      // All printers from backend only have 'online' status
      if (!printer.online) return 'grey darken-3';
      return 'green darken-4';
    },
    
    getPrinterStatusColor(printer) {
      if (!printer.online) return 'grey';
      if (printer.error) return 'error';
      if (printer.warning) return 'warning';
      if (printer.printing) return 'success';
      return 'info';
    },
    
    getPrinterStatusText(printer) {
      if (!printer.online) return 'Offline';
      if (printer.error) return 'Fehler';
      if (printer.warning) return 'Warnung';
      if (printer.printing) return 'Druckt';
      return 'Bereit';
    },
    
    getProgressColor(progress) {
      if (progress < 30) return 'red';
      if (progress < 70) return 'orange';
      return 'green';
    },
    
    formatTime(timestamp) {
      if (!timestamp) return 'Nie';
      const date = new Date(timestamp);
      const now = new Date();
      const diff = now - date;
      
      if (diff < 60000) return 'Gerade eben';
      if (diff < 3600000) return `Vor ${Math.floor(diff / 60000)} Min`;
      if (diff < 86400000) return `Vor ${Math.floor(diff / 3600000)} Std`;
      return date.toLocaleDateString('de-DE');
    },
    
    updateCharts() {
      if (!this.printers || this.printers.length === 0) return;
      
      // Update job chart
      const categories = this.printers.map(p => p.name);
      const successful = this.printers.map(p => p.successfulJobs || 0);
      const queued = this.printers.map(p => p.queue || 0);
      const errors = this.printers.map(p => p.errorJobs || 0);
      
      this.jobChartOptions = {
        ...this.jobChartOptions,
        xaxis: {
          ...this.jobChartOptions.xaxis,
          categories: categories
        }
      };
      
      this.jobChartSeries = [
        { name: 'Erfolgreich', data: successful },
        { name: 'In Warteschlange', data: queued },
        { name: 'Fehler', data: errors }
      ];
    },
    
    updatePrintHistoryChart() {
      if (!this.printHistory || this.printHistory.length === 0) {
        this.printChartSeries = [];
        return;
      }
      
      // Group by printer and create time series
      const printerData = {};
      
      this.printHistory.forEach(point => {
        const printer = point.printer || 'Unknown';
        if (!printerData[printer]) {
          printerData[printer] = [];
        }
        printerData[printer].push({
          x: new Date(point.timestamp).getTime(),
          y: point.count || 0
        });
      });
      
      this.printChartSeries = Object.keys(printerData).map(printer => ({
        name: printer,
        data: printerData[printer]
      }));
    }
  }
};
</script>

<style scoped>
.printers-card {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%);
}

.section-title {
  font-size: 1.2rem;
  font-weight: 500;
}

.printer-item {
  transition: all 0.3s ease;
  border-radius: 8px;
}

.printer-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.printer-name {
  font-size: 1rem;
}

.status-chip {
  font-weight: bold;
  text-transform: uppercase;
  font-size: 0.7rem;
}

.printer-stats {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 8px;
}

.metric-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}
</style>